// @ts-nocheck
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkles, Loader2, FileText, MessageSquare, Copy, Check, Download, Save, Filter } from 'lucide-react';
import { api } from '@/api/api-client';
import { saveLibraryScript } from '@/components/dialer/callScriptLibrary';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDialer } from '@/contexts/DialerContext';
import { hasMeetingBooked } from '@/utils/callUtils';

// ─── Period helpers ────────────────────────────────────────────────────────────
// Labels resolved from the catalog at render time (see `periodOptions`
// below); only the values and the `label` used by buildCallContext (not
// user-facing, an LLM prompt fragment) stay fixed here.
const PERIOD_VALUES = ['today', '7d', '30d', '90d', 'all'];
const PERIOD_LABEL_KEY = {
    today: 'common:time.today',
    '7d': 'dialer:callHistory.period.last7Days',
    '30d': 'dialer:callHistory.period.last30Days',
    '90d': 'dialer:aiAssistant.period.last90Days',
    all: 'dialer:callHistory.period.all',
};

function getPeriodStart(period) {
    const now = new Date();
    switch (period) {
        case 'today': {
            const d = new Date(now);
            d.setHours(0, 0, 0, 0);
            return d;
        }
        case '7d':  return new Date(now - 7  * 86400_000);
        case '30d': return new Date(now - 30 * 86400_000);
        case '90d': return new Date(now - 90 * 86400_000);
        default:    return null; // all
    }
}

function buildCallContext(calls, selectedSource, period) {
    let filtered = [...calls];

    // Source filter
    if (selectedSource !== 'all') {
        filtered = filtered.filter(c => (c.source || 'Revcall Client Dashboard') === selectedSource);
    }

    // Period filter
    const periodStart = getPeriodStart(period);
    if (periodStart) {
        filtered = filtered.filter(c => new Date(c.created_date) >= periodStart);
    }

    if (filtered.length === 0) {
        return 'Aucun appel dans cette sélection.';
    }

    const total      = filtered.length;
    const connected  = filtered.filter(c => hasMeetingBooked(c) || ['contacted', 'connected', 'connected_to_agent', 'not_interested', 'callback_requested'].includes(c.call_outcome)).length;
    const meetings   = filtered.filter(hasMeetingBooked).length;
    const noAnswer   = filtered.filter(c => c.call_outcome === 'no_answer').length;
    const voicemail  = filtered.filter(c => c.call_outcome === 'voicemail').length;
    const callbacks  = filtered.filter(c => c.call_outcome === 'callback_requested').length;
    const connectRate = total > 0 ? ((connected / total) * 100).toFixed(1) : '0';

    // Unique companies / sectors
    const companies = [...new Set(filtered.map(c => c.prospect_company).filter(Boolean))];
    const companySample = companies.slice(0, 10).join(', ');

    // Recent notes
    const notes = filtered
        .filter(c => c.notes)
        .slice(-5)
        .map(c => `- ${c.prospect_name || 'Prospect'} (${c.prospect_company || 'N/A'}): ${c.notes}`)
        .join('\n');

    // Not translated: this line feeds the LLM prompt, not the screen (see
    // `buildCallContext`'s caller) -- a fixed French label per period value
    // is enough context for the model, independent of the UI's language.
    // i18n-exempt-start: context for the model (see above)
    const periodContextLabel = {
        today: "aujourd'hui", '7d': '7 derniers jours', '30d': '30 derniers jours',
        '90d': '90 derniers jours', all: 'toute la période',
    }[period] || period;
    // i18n-exempt-end
    const lines = [
        `Périmètre analysé: ${selectedSource === 'all' ? 'toutes les listes' : selectedSource}, ${periodContextLabel}`,
        `Total appels: ${total}`,
        `Taux de contact: ${connectRate}% (${connected}/${total})`,
        `Rendez-vous obtenus: ${meetings}`,
        `Pas de réponse: ${noAnswer}`,
        `Messageries: ${voicemail}`,
        `Rappels demandés: ${callbacks}`,
        companySample ? `Entreprises contactées (échantillon): ${companySample}` : null,
        notes ? `Notes récentes:\n${notes}` : null,
    ].filter(Boolean);

    return lines.join('\n');
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AIAssistant({ clientEmail }) {
    const { t } = useTranslation(['dialer', 'common', 'pages']);
    const { sources, allCalls } = useDialer();
    const periodOptions = PERIOD_VALUES.map((value) => ({ value, label: t(PERIOD_LABEL_KEY[value]) }));

    const [prompt, setPrompt] = useState('');
    const [response, setResponse] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [mode, setMode] = useState('assistant'); // 'assistant' | 'script' | 'document'
    const [copied, setCopied] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [scriptName, setScriptName] = useState('');
    const [scriptDescription, setScriptDescription] = useState('');

    // ── Filter state ───────────────────────────────────────────────────────────
    const [selectedSource, setSelectedSource] = useState('all');
    const [selectedPeriod, setSelectedPeriod] = useState('30d');

    const queryClient = useQueryClient();

    const modes = [
        {
            id: 'assistant',
            label: t('dialer:aiAssistant.modes.assistant.label'),
            icon: MessageSquare,
            prompt: t('dialer:aiAssistant.modes.assistant.prompt'),
            placeholder: t('dialer:aiAssistant.modes.assistant.placeholder')
        },
        {
            id: 'script',
            label: t('dialer:aiAssistant.modes.script.label'),
            icon: FileText,
            prompt: t('dialer:aiAssistant.modes.script.prompt'),
            placeholder: t('dialer:aiAssistant.modes.script.placeholder')
        },
        {
            id: 'document',
            label: t('dialer:aiAssistant.modes.document.label'),
            icon: FileText,
            prompt: t('dialer:aiAssistant.modes.document.prompt'),
            placeholder: t('dialer:aiAssistant.modes.document.placeholder')
        }
    ];

    const currentMode = modes.find(m => m.id === mode);

    const handleGenerate = async () => {
        if (!prompt.trim()) {
            toast.error(t('dialer:aiAssistant.toast.emptyPrompt'));
            return;
        }

        setIsGenerating(true);
        setResponse('');

        try {
            // Build call data context from selected filters
            const callContext = buildCallContext(allCalls, selectedSource, selectedPeriod);

            let systemPrompt = '';

            if (mode === 'assistant') {
                systemPrompt = `Tu es un expert en prospection téléphonique et vente B2B.
Tu as accès aux données réelles de la campagne en cours:

${callContext}

Réponds de manière concise, pratique et directement applicable en tenant compte de ces données.
Donne des exemples concrets et des phrases prêtes à l'emploi.`;
            } else if (mode === 'script') {
                systemPrompt = `Tu es un expert en création de scripts d'appel commerciaux.
Tu as accès aux données réelles de la campagne en cours:

${callContext}

Crée un script détaillé adapté au contexte de cette campagne, structuré avec:
- Une accroche percutante adaptée aux entreprises contactées
- Des questions de qualification pertinentes
- Des réponses aux objections courantes identifiées dans les notes
- Un call-to-action clair
Format le script de manière claire avec des sections identifiables.`;
            } else if (mode === 'document') {
                systemPrompt = `Tu es un expert en rédaction commerciale.
Tu as accès aux données réelles de la campagne en cours:

${callContext}

Crée un document professionnel, bien structuré et adapté au contexte de cette campagne.
Adapte le ton et le style selon le type de document demandé.`;
            }

            const result = await api.integrations.Core.InvokeLLM({
                feature: 'ai_assistant',
                prompt: `${systemPrompt}\n\nDemande: ${prompt}`,
                add_context_from_internet: false
            });

            const text = typeof result === 'string' ? result : result?.result || result?.text || result?.content || JSON.stringify(result, null, 2);
            setResponse(text);
            toast.success(t('dialer:aiAssistant.toast.generated'));
        } catch (error) {
            toast.error(t('dialer:aiAssistant.toast.generateError'));
            console.error(error);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(response);
        setCopied(true);
        toast.success(t('dialer:callScripts.toast.copied'));
        setTimeout(() => setCopied(false), 2000);
    };

    const handleDownload = () => {
        const blob = new Blob([response], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${mode}_${Date.now()}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(t('dialer:aiAssistant.toast.downloaded'));
    };

    const saveMutation = useMutation({
        mutationFn: async () => {
            return saveLibraryScript({ name: scriptName, content: response, aiGenerated: true });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['call-scripts'] });
            setShowSaveDialog(false);
            setScriptName('');
            setScriptDescription('');
            toast.success(t('dialer:aiAssistant.toast.scriptSaved'));
        },
        onError: () => {
            toast.error(t('dialer:callScripts.toast.saveError'));
        }
    });

    const handleSave = () => {
        if (!scriptName) {
            toast.error(t('dialer:callScripts.toast.nameRequired'));
            return;
        }
        saveMutation.mutate();
    };

    const quickPrompts = {
        assistant: [
            t('dialer:aiAssistant.quickPrompts.assistant1'),
            t('dialer:aiAssistant.quickPrompts.assistant2'),
            t('dialer:aiAssistant.quickPrompts.assistant3'),
        ],
        script: [
            t('dialer:aiAssistant.quickPrompts.script1'),
            t('dialer:aiAssistant.quickPrompts.script2'),
            t('dialer:aiAssistant.quickPrompts.script3'),
        ],
        document: [
            t('dialer:aiAssistant.quickPrompts.document1'),
            t('dialer:aiAssistant.quickPrompts.document2'),
            t('dialer:aiAssistant.quickPrompts.document3'),
        ]
    };

    // Source options: "all" + each distinct source from prospect queue
    const sourceOptions = [
        { value: 'all', label: t('dialer:callHistory.filters.allLists') },
        ...sources.map(s => ({ value: s, label: s })),
    ];

    return (
        <Card className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
            {/* No card title (W4, B2-tools): the page is « Assistant IA » and
                the tab says what this card does; a third « Assistant IA »
                here said nothing more. */}
            <CardContent className="p-6 space-y-4">
                {/* Mode Selection */}
                <div className="flex gap-2">
                    {modes.map((m) => {
                        const Icon = m.icon;
                        return (
                            <button
                                key={m.id}
                                onClick={() => {
                                    setMode(m.id);
                                    setPrompt('');
                                    setResponse('');
                                }}
                                className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 font-medium text-sm ${
                                    mode === m.id
                                        ? 'bg-[var(--vf-accent-bg)] border-[var(--vf-accent)] text-[var(--vf-accent-text)]'
                                        : 'bg-card border-border text-muted-foreground hover:bg-accent/50'
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                {m.label}
                            </button>
                        );
                    })}
                </div>

                {/* Context Filters */}
                <div className="flex gap-3 p-3 bg-muted/40 border border-border rounded-xl">
                    <Filter className="w-4 h-4 text-muted-foreground mt-2.5 shrink-0" />
                    <div className="flex-1 flex gap-3 flex-wrap">
                        <div className="flex-1 min-w-[160px]">
                            <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('dialer:aiAssistant.listLabel')}</label>
                            <Select value={selectedSource} onValueChange={setSelectedSource}>
                                <SelectTrigger className="h-8 text-sm bg-card border-border">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {sourceOptions.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1 min-w-[160px]">
                            <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('pages:callHistory.filters.dateRange')}</label>
                            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                                <SelectTrigger className="h-8 text-sm bg-card border-border">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {periodOptions.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2.5 shrink-0">
                        {(() => {
                            let filtered = [...allCalls];
                            if (selectedSource !== 'all') {
                                filtered = filtered.filter(c => (c.source || 'Revcall Client Dashboard') === selectedSource);
                            }
                            const periodStart = getPeriodStart(selectedPeriod);
                            if (periodStart) {
                                filtered = filtered.filter(c => new Date(c.created_date) >= periodStart);
                            }
                            return t('dialer:aiAssistant.callsCount', { count: filtered.length });
                        })()}
                    </div>
                </div>

                {/* Input Area */}
                <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground/80 block">
                        {currentMode?.prompt}
                    </label>
                    <Textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder={currentMode?.placeholder}
                        rows={4}
                        className="surface-1 border-border"
                    />

                    {/* Quick Prompts */}
                    <div className="flex flex-wrap gap-2">
                        {quickPrompts[mode]?.map((quick, idx) => (
                            <button
                                key={idx}
                                onClick={() => setPrompt(quick)}
                                className="text-xs px-3 py-1.5 bg-muted hover:bg-accent text-foreground/80 rounded-full transition-colors"
                            >
                                {quick}
                            </button>
                        ))}
                    </div>

                    <Button
                        onClick={handleGenerate}
                        disabled={isGenerating || !prompt.trim()}
                        className="bg-[var(--vf-primary)] hover:bg-[var(--vf-primary-hover)] w-full text-white h-11"
                    >
                        {isGenerating ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {t('dialer:aiAssistant.generating')}
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-4 h-4 mr-2" />
                                {t('dialer:aiAssistant.generateButton')}
                            </>
                        )}
                    </Button>
                </div>

                {/* Response Area */}
                <AnimatePresence>
                    {response && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-3"
                        >
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-foreground/80">{t('dialer:aiAssistant.resultLabel')}</label>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCopy}
                                        className="border-border"
                                    >
                                        {copied ? (
                                            <>
                                                <Check className="w-4 h-4 mr-1 text-green-600" />
                                                {t('common:actions.copied')}
                                            </>
                                        ) : (
                                            <>
                                                <Copy className="w-4 h-4 mr-1" />
                                                {t('common:actions.copy')}
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleDownload}
                                        className="border-border"
                                    >
                                        <Download className="w-4 h-4 mr-1" />
                                        {t('common:actions.download')}
                                    </Button>
                                    {mode === 'script' && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowSaveDialog(true)}
                                            className="border-green-200 text-green-600 hover:bg-green-50"
                                        >
                                            <Save className="w-4 h-4 mr-1" />
                                            {t('dialer:callScripts.save')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="bg-[var(--vf-accent-bg)] border border-border rounded-xl p-6 max-h-[500px] overflow-y-auto">
                                <ReactMarkdown
                                    className="prose prose-sm prose-slate max-w-none
                                        prose-headings:text-foreground prose-headings:font-semibold
                                        prose-p:text-foreground/80 prose-p:leading-relaxed
                                        prose-strong:text-foreground prose-strong:font-semibold
                                        prose-ul:text-foreground/80 prose-ol:text-foreground/80
                                        prose-li:marker:text-[var(--vf-accent-text)]"
                                >
                                    {response}
                                </ReactMarkdown>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </CardContent>

            {/* Save Dialog */}
            <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('dialer:aiAssistant.saveDialogTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">{t('dialer:callScripts.scriptNameLabel')}</label>
                            <Input
                                value={scriptName}
                                onChange={(e) => setScriptName(e.target.value)}
                                placeholder={t('dialer:callScripts.namePlaceholder')}
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-2 block">{t('dialer:callScripts.descriptionLabel')}</label>
                            <Input
                                value={scriptDescription}
                                onChange={(e) => setScriptDescription(e.target.value)}
                                placeholder={t('dialer:callScripts.descriptionPlaceholder')}
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={() => setShowSaveDialog(false)}
                                variant="outline"
                                className="flex-1"
                            >
                                {t('common:actions.cancel')}
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saveMutation.isPending}
                                className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                                {saveMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                )}
                                {t('dialer:callScripts.save')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
